import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  Alert,
  Button,
  Box,
  Typography,
  Avatar,
  Divider
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import { 
  useGlobalSettingState, 
  useComponentWebSocket,
  textModelOptions,
  visionModelOptions,
  audioModelOptions
} from './GlobalSettingUtils';
import { providerIcons } from './GlobalSettingUtils';

const ModelSettings = forwardRef((props, ref) => {
  const { onConfigChange, savedModels } = props;
  const { 
    isModified, 
    setIsModified, 
    t,
    openSnackbar
  } = useGlobalSettingState();
  const [isSavingModelSettings, setIsSavingModelSettings] = useState(false);
  
  // Use shared providerIcons mapping for icon/description/color

  const [config, setConfig] = useState({
    modelTextHost: '',
    modelTextHostId: '',
    modelVisionHost: '',
    modelVisionHostId: '',
    modelAudioHost: '',
    modelAudioHostId: '',
    modelWidgetHost: '',
    modelWidgetHostId: '',
    modelWidgetVisualHost: '',
    modelWidgetVisualHostId: '',
    modelProjectHost: '',
    modelProjectHostId: '',
    modelPluginCreatorHost: '',
    modelPluginCreatorHostId: '',
  });
  
  const modelFieldMap = {
    text: { providerField: 'modelTextHost', idField: 'modelTextHostId' },
    vision: { providerField: 'modelVisionHost', idField: 'modelVisionHostId' },
    audio: { providerField: 'modelAudioHost', idField: 'modelAudioHostId' },
    widget: { providerField: 'modelWidgetHost', idField: 'modelWidgetHostId' },
    widget_visual: { providerField: 'modelWidgetVisualHost', idField: 'modelWidgetVisualHostId' },
    project: { providerField: 'modelProjectHost', idField: 'modelProjectHostId' },
    plugin_creator: { providerField: 'modelPluginCreatorHost', idField: 'modelPluginCreatorHostId' }
  };
  
  // Helper function to update settings from WebSocket data
  const updateSettings = useCallback((data) => {
    console.log('ModelSettings received configuration:', data);
    setConfig({
      modelTextHost: data.modelTextHost || '',
      modelTextHostId: data.modelTextHostId ? String(data.modelTextHostId) : '',
      modelVisionHost: data.modelVisionHost || '',
      modelVisionHostId: data.modelVisionHostId ? String(data.modelVisionHostId) : '',
      modelAudioHost: data.model_audio_host || data.modelAudioHost || '',
      modelAudioHostId: data.modelAudioHostId ? String(data.modelAudioHostId) : '',
      modelWidgetHost: data.modelWidgetHost || '',
      modelWidgetHostId: data.modelWidgetHostId ? String(data.modelWidgetHostId) : '',
      modelWidgetVisualHost: data.modelWidgetVisualHost || '',
      modelWidgetVisualHostId: data.modelWidgetVisualHostId ? String(data.modelWidgetVisualHostId) : '',
      modelProjectHost: data.modelProjectHost || '',
      modelProjectHostId: data.modelProjectHostId ? String(data.modelProjectHostId) : '',
      modelPluginCreatorHost: data.modelPluginCreatorHost || '',
      modelPluginCreatorHostId: data.modelPluginCreatorHostId ? String(data.modelPluginCreatorHostId) : '',
    });
  }, []);

  // Define handlers for WebSocket messages
  const commandHandlers = useMemo(() => ({
    'update_configuration': (message) => {
      setIsSavingModelSettings(false);
      if (message.data === 'success') {
        openSnackbar(t('global_setting.save_completed'), 3000);
        setIsModified(false);
      }
    },
    'model_operation_error': (message) => {
      setIsSavingModelSettings(false);
      openSnackbar(message.data?.error || t('global_setting.model_operation_error', 'Error performing operation'), 5000);
    },
    'configuration_data': (message) => {
      updateSettings(message.data);
    },
    'text_model_settings': (message) => {
      updateSettings(message.data);
    },
    'vision_model_settings': (message) => {
      updateSettings(message.data);
    },
    'audio_model_settings': (message) => {
      updateSettings(message.data);
    }
  }), [updateSettings, openSnackbar, t, setIsModified]);
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  // Expose getModelSettings method to parent component
  useImperativeHandle(ref, () => ({
    getModelSettings: () => {
      return {
        modelTextHost: config.modelTextHost,
        modelTextHostId: config.modelTextHostId,
        modelVisionHost: config.modelVisionHost,
        modelVisionHostId: config.modelVisionHostId,
        model_audio_host: config.modelAudioHost,
        modelAudioHostId: config.modelAudioHostId,
        modelWidgetHost: config.modelWidgetHost,
        modelWidgetHostId: config.modelWidgetHostId,
        modelWidgetVisualHost: config.modelWidgetVisualHost,
        modelWidgetVisualHostId: config.modelWidgetVisualHostId,
        modelProjectHost: config.modelProjectHost,
        modelProjectHostId: config.modelProjectHostId,
        modelPluginCreatorHost: config.modelPluginCreatorHost,
        modelPluginCreatorHostId: config.modelPluginCreatorHostId,
      };
    }
  }));

  // Load settings when component mounts
  useEffect(() => {
    sendCommand('configuration_check');
  }, [sendCommand]);

  const handleModelSelection = (modelType, savedModelsList = []) => (event) => {
    const fields = modelFieldMap[modelType];
    if (!fields) return;
    const selectedValueRaw = event.target.value;
    const selectedValue = selectedValueRaw === undefined || selectedValueRaw === null
      ? ''
      : String(selectedValueRaw);
    
    const selectedModel = savedModelsList.find((model) => {
      if (selectedValue === '') return false;
      if (model.id !== undefined && model.id !== null) {
        return String(model.id) === selectedValue;
      }
      return model.provider === selectedValue;
    });

    const providerValue = selectedValue === '' ? '' : (selectedModel?.provider || '');

    setConfig((prevConfig) => {
      const newConfig = {
        ...prevConfig,
        [fields.idField]: selectedValue,
        [fields.providerField]: providerValue
      };
      if (onConfigChange) {
        onConfigChange({
          [fields.idField]: selectedValue,
          [fields.providerField]: providerValue
        });
      }
      return newConfig;
    });
    setIsModified(true);
  };

  const handleSave = () => {
    if (isModified) {
      if (isSavingModelSettings) return;
      setIsSavingModelSettings(true);
      const settings = {
        modelTextHost: config.modelTextHost,
        modelTextHostId: config.modelTextHostId,
        modelVisionHost: config.modelVisionHost,
        modelVisionHostId: config.modelVisionHostId,
        model_audio_host: config.modelAudioHost,
        modelAudioHostId: config.modelAudioHostId,
        modelWidgetHost: config.modelWidgetHost,
        modelWidgetHostId: config.modelWidgetHostId,
        modelWidgetVisualHost: config.modelWidgetVisualHost,
        modelWidgetVisualHostId: config.modelWidgetVisualHostId,
        modelProjectHost: config.modelProjectHost,
        modelProjectHostId: config.modelProjectHostId,
        modelPluginCreatorHost: config.modelPluginCreatorHost,
        modelPluginCreatorHostId: config.modelPluginCreatorHostId,
      };
      
      // Send model settings to the server
      sendCommand('update_configuration', settings);
      
      console.log('Model settings sent:', settings);
    } else {
      console.error('No changes were made.');
    }
  };

  const renderModelDropdown = (modelType, label, selectedId, selectedProvider, options, savedModelsList) => {
    // Add "Disabled" option at the beginning
    const disabledOption = {
      value: '',
      label: t('global_setting.disabled', 'Disabled'),
      accuracy: 'N/A',
      accuracyColor: '#757575'
    };
    const normalizedValue = selectedId ? String(selectedId) : '';
    const modelsForType = savedModelsList || [];
    const selectionHandler = handleModelSelection(modelType, modelsForType);
    
    const renderSelectedValue = (selected) => {
      if (!selected || selected === '') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar 
                src={providerIcons['none']?.icon}
              sx={{ 
                width: 24, 
                height: 24, 
                bgcolor: 'transparent'
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('global_setting.disabled', 'Disabled')}
            </Typography>
          </Box>
        );
      }
      
      let model = modelsForType.find(m => m.id !== undefined && m.id !== null && String(m.id) === String(selected));
      if (!model && selectedProvider) {
        model = modelsForType.find(m => m.provider === selectedProvider);
      }
      
        if (!model) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('global_setting.no_model_configured', 'No model configured - Go to Model Management tab')}
            </Typography>
          </Box>
        );
      }
      
      const modelOption = options.find(opt => opt.value === model.provider);
      const info = providerIcons[model.provider] || providerIcons['local'];
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar 
            src={info?.icon}
            sx={{ 
              width: 24, 
              height: 24, 
              bgcolor: 'transparent'
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2">
              {modelOption?.label || model.provider}
            </Typography>
            {model.model_name && (
              <Typography 
                variant="caption" 
                color="text.secondary"
              >
                {t('global_setting.model_name', 'Model')}: {model.model_name}
              </Typography>
            )}
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 'bold',
                color: modelOption?.accuracyColor || '#5757b0'
              }}
            >
              {modelOption?.accuracy || ''}
            </Typography>
          </Box>
        </Box>
      );
    };
    
    return (
      <>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#5757b0', mb: 1 }}>
              {label}
            </Typography>
            <Tooltip title={t(`global_setting.model_${modelType}_tooltip`, 
              modelType === 'text' ? 'AI model used for general text processing and document analysis' :
              modelType === 'vision' ? 'AI model used for image analysis and visual content extraction' :
              modelType === 'audio' ? 'AI model used for audio transcription and processing' :
              modelType === 'widget' ? 'AI model used specifically for widget generation and aggregation tasks' :
              modelType === 'plugin_creator' ? 'AI model used for AI-assisted plugin planning, generation, and verification' :
              'AI model used for project creation and tag generation'
            )}>
              <IconButton size="small" sx={{ mb: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            label={t(`global_setting.model_${modelType}_host_label`, `Select ${label}`)}
            value={normalizedValue}
            onChange={selectionHandler}
            fullWidth
            SelectProps={{
              renderValue: renderSelectedValue
            }}
          >
            {/* Disabled option */}
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar 
                      src={providerIcons['none']?.icon}
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: 'transparent'
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {t(`global_setting.model_label_${disabledOption.value}`, disabledOption.label)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('global_setting.disabled_description', `${modelType} model will not be used`)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            
            {/* Saved models */}
            {modelsForType && modelsForType.length > 0 && modelsForType.map((model) => {
              const info = providerIcons[model.provider] || providerIcons['local'];
              const option = options.find(opt => opt.value === model.provider);
              const optionValue = model.id !== undefined && model.id !== null
                ? String(model.id)
                : model.provider;
              return (
                <MenuItem key={model.id ? `${model.id}` : `${model.provider}-${modelType}`} value={optionValue}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar 
                      src={info?.icon}
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'transparent'
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {t(`global_setting.model_label_${option?.value}`, option?.label || model.provider)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 'bold', 
                            ml: 1,
                            color: option?.accuracyColor || '#5757b0'
                          }}
                        >
                          {option?.accuracy || ''}
                        </Typography>
                      </Box>
                      {model.model_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {t('global_setting.model_name', 'Model')}: {model.model_name}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {info?.description || `${modelType} model option`}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
            
            {/* No models message */}
            {(!savedModelsList || savedModelsList.length === 0) && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {t('global_setting.no_models_available', 'No models configured. Please add models in the Model Management tab.')}
                </Typography>
              </MenuItem>
            )}
          </TextField>
        </Grid>
      </>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('global_setting.model_selection_info', 'Select which AI model configurations to use for each type. To add or modify model configurations, go to the Model Management tab.')}
        </Alert>
      </Grid>

      {/* Text Model */}
      {renderModelDropdown('text', t('global_setting.text_model', 'Default Text Model'), config.modelTextHostId, config.modelTextHost, textModelOptions, savedModels?.text)}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Vision Model */}
      {renderModelDropdown('vision', t('global_setting.vision_model', 'Default Vision Model'), config.modelVisionHostId, config.modelVisionHost, visionModelOptions, savedModels?.vision)}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Audio Model */}
      {renderModelDropdown('audio', t('global_setting.audio_model', 'Default Audio Model'), config.modelAudioHostId, config.modelAudioHost, audioModelOptions, savedModels?.audio)}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Widget Model (text models only) */}
      {renderModelDropdown('widget', t('global_setting.widget_model', 'Widget Generation Model'), config.modelWidgetHostId, config.modelWidgetHost, textModelOptions, savedModels?.text)}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Widget Visual Model (text models only) */}
      {renderModelDropdown('widget_visual', t('global_setting.widget_visual_model', 'Widget Generation Visual Model'), config.modelWidgetVisualHostId, config.modelWidgetVisualHost, textModelOptions, savedModels?.text)}

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Project Model (text models only) */}
      {renderModelDropdown('project', t('global_setting.project_model', 'Project Creation Model'), config.modelProjectHostId, config.modelProjectHost, textModelOptions, savedModels?.text)}

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* Plugin Creator Model (text models only) */}
      {renderModelDropdown('plugin_creator', t('global_setting.plugin_creator_model', 'Plugin Creator Model'), config.modelPluginCreatorHostId, config.modelPluginCreatorHost, textModelOptions, savedModels?.text)}
      
      <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!isModified || isSavingModelSettings}
          sx={{
            backgroundColor: '#5757b0',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#aaaaca',
            },
            py: 1,
            px: 3,
            borderRadius: 2,
          }}
        >
          {isSavingModelSettings ? t('global_setting.saving', 'Saving...') : (t('global_setting.save_button') || 'Save Model Settings')}
        </Button>
      </Grid>
    </Grid>
  );
});

export { ModelSettings };
